






















































import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import CGoogleAutocompleteInput from '~/components/shared/configurable/form/autocomplete/CGoogleAutocompleteInput.vue'
import { Geolocation } from '~/models/common/types'
import CGoogleMap from '~/components/shared/configurable/map/CGoogleMap.vue'
import { useDeps } from '~/compositions/dependency-container'
import GoogleMapsService from '~/services/GoogleMapsService'
import { FullLocation } from '~/models/google'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import PostcodeService from '~/services/location/PostcodeService'
import { CountryCode } from '~/models/location/country'

export default defineComponent({
  props: {
    marker: {
      type: Object as PropType<Geolocation>,
      default: null
    },
    address: {
      type: String,
      default: null
    },
    zoom: {
      type: Number,
      default: 5
    },
    initialZoom: {
      type: Number,
      default: undefined
    },
    postcode: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: ''
    },
    altError: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: undefined
    }
  },
  components: {
    CFormError,
    CGoogleAutocompleteInput,
    CGoogleMap
  },
  setup(props, { emit }) {
    const { marker, address, postcode, error, required } = toRefs(props)

    const [googleMapsService, postcodeService] = useDeps(
      GoogleMapsService,
      PostcodeService
    )

    const internalMarker = ref(marker.value)
    const internalAddress = ref<string | null>(address.value)
    const internalPostcode = ref<string | null>(postcode.value)
    const internalCity = ref<string | null>(null)
    const internalCountry = ref<string | null>(null)

    async function mapMarkerLocationChanged(geolocation: Geolocation) {
      const {
        address,
        city,
        postcode,
        country
      } = await googleMapsService.getGeocodingProperties(geolocation)

      internalAddress.value = address
      internalPostcode.value = await getPostcodeWithClosestFallback(
        postcode,
        geolocation,
        country as CountryCode
      )
      internalMarker.value = geolocation
      internalCity.value = city
      internalCountry.value = country
      emitData()
    }

    async function inputChanged({
      geolocation,
      postcode,
      city,
      address,
      country
    }: FullLocation) {
      internalAddress.value = address
      internalPostcode.value = await getPostcodeWithClosestFallback(
        postcode,
        geolocation,
        country as CountryCode
      )
      internalCity.value = city
      internalMarker.value = geolocation
      internalCountry.value = country
      emitData()
    }

    function emitData() {
      emit('change', {
        geolocation: internalMarker.value,
        postcode: internalPostcode.value,
        address: internalAddress.value,
        city: internalCity.value,
        country: internalCountry.value
      })
    }

    async function getPostcodeWithClosestFallback(
      postcode: string | null,
      geolocation: Geolocation,
      countryCode: CountryCode
    ) {
      // Do not attempt to get the nearest postcode for other than GR locations
      if (postcode || countryCode !== CountryCode.GR) {
        return postcode
      }
      const {
        postcode: closestPostcode
      } = await postcodeService.getClosestPostcode(geolocation)
      return closestPostcode?.name || postcode
    }

    const isValid = computed(() => !error.value)
    const labelClass = computed(() =>
      required.value
        ? ['tw-font-bold', "after:tw-content-['*']", 'after:tw-ml-0.5']
        : []
    )

    watch(marker, newMarker => (internalMarker.value = newMarker))
    watch(address, newMarker => (internalAddress.value = newMarker))
    watch(postcode, newMarker => (internalPostcode.value = newMarker))

    return {
      mapMarkerLocationChanged,
      internalAddress,
      internalPostcode,
      internalMarker,
      internalCountry,
      inputChanged,
      isValid,
      labelClass
    }
  }
})
